import {
  NavigationContainerRef,
  NavigationState,
  PartialRoute,
  StackActions,
} from '@react-navigation/native';
import React from 'react';

import { getScreenSize } from 'lib/riskbase';
import { ScreenSize } from 'lib/constants';
import { NavigationStacks, Stacks } from './types';

export const navigationRef =
  React.createRef<NavigationContainerRef<NavigationStacks[Stacks.ROOT]>>();

export const push = <ST extends Stacks, SC extends keyof NavigationStacks[ST]>(
  stack: ST,
  screen: SC,
  params: NavigationStacks[ST][SC],
  // @ts-expect-error it works...
) => navigationRef.current?.dispatch(StackActions.push(screen, params));

export const navigate = <
  ST extends Stacks,
  SC extends keyof NavigationStacks[ST],
>(
  stack: ST,
  screen: SC,
  params: NavigationStacks[ST][SC],
  // @ts-expect-error it works...
) => navigationRef.current?.navigate(screen, params);

export const replace = <
  ST extends Stacks,
  SC extends keyof NavigationStacks[ST],
>(
  stack: ST,
  screen: SC,
  params: NavigationStacks[ST][SC],
  // @ts-expect-error it works...
) => navigationRef.current?.dispatch(StackActions.replace(screen, params));

export const navigateScreenAware = <
  ST extends Stacks,
  SC extends keyof NavigationStacks[ST],
>(
  stack: ST,
  screen: SC,
  params: NavigationStacks[ST][SC],
) =>
  getScreenSize().size === ScreenSize.small
    ? navigate(stack, screen, params)
    : replace(stack, screen, params);

export function navigationIsSelected(
  screens: {
    name?: string;
    params?: Record<string, unknown>;
  }[],
) {
  const cascade = (
    routes:
      | NavigationState['routes']
      | PartialRoute<NavigationState['routes'][number]>[],
    name: string,
    params?: Record<string, unknown>,
  ): boolean => {
    let found = false;

    for (const route of routes) {
      if (route.name === name) {
        if (params === undefined) {
          found = true;
          break;
        } else {
          const routeParams = route.params as
            | Record<string, unknown>
            | undefined;
          if (routeParams) {
            let allMatch = true;

            for (const paramKey in params) {
              if (routeParams[paramKey] !== params[paramKey]) {
                allMatch = false;
                break;
              }
            }
            if (allMatch) {
              found = true;
              break;
            }
          }
        }
      }
      if (
        route.name === name &&
        (params === undefined ||
          Object.keys(params).every((paramKey) => {
            const routeParams = route.params as
              | Record<string, unknown>
              | undefined;
            return routeParams && routeParams[paramKey] === params[paramKey];
          }))
      ) {
        found = true;
        break;
      }
      if (!found && route.state?.routes) {
        const didFind = cascade(
          route.state.routes as NavigationState['routes'],
          name,
          params,
        );
        if (didFind) {
          found = true;
        }
      }
    }
    return found;
  };

  const routes = navigationRef.current?.getRootState()?.routes;
  if (routes) {
    return screens.some(
      (screen) => !!screen.name && cascade(routes, screen.name, screen.params),
    );
  }
  return false;
}
